// System notifications (the computer's own banners) for Atrium in a browser tab. The desktop app
// raises its own through the Electron bridge (lib/desktop.ts), so useDesktopIntegration only calls
// these when that bridge is missing. They show while Atrium is open in a tab, even a background one.
import { useSyncExternalStore } from 'react';
import { useUi } from './ui';

export type NotifyPermission = NotificationPermission | 'unsupported';

const supported = () => typeof window !== 'undefined' && 'Notification' in window;

export function notifyPermission(): NotifyPermission {
  return supported() ? Notification.permission : 'unsupported';
}

// re-render when the permission changes: after our own request, or in the browser's site settings
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());
let watching = false;
function subscribe(cb: () => void) {
  listeners.add(cb);
  if (!watching && supported()) {
    watching = true;
    window.addEventListener('focus', changed);
    navigator.permissions
      ?.query({ name: 'notifications' })
      .then((status) => status.addEventListener('change', changed))
      .catch(() => {});
  }
  return () => {
    listeners.delete(cb);
  };
}

export function useNotifyPermission() {
  return useSyncExternalStore(subscribe, notifyPermission);
}

/** Ask the browser for permission (must run from a click). Turns notifications on when granted. */
export async function requestNotifyPermission(): Promise<NotifyPermission> {
  if (!supported()) return 'unsupported';
  const result = await Notification.requestPermission();
  changed();
  if (result === 'granted') useUi.setState({ notify: true });
  return result;
}

// With several tabs open only one shows notifications: the first tab to take this lock keeps it
// until it closes, then the next one takes over. Without the Web Locks API every tab shows them,
// and the per-notification `tag` collapses the duplicates on most systems.
let leader = false;
let claimed = false;
export function claimNotifications() {
  if (claimed || !supported()) return;
  claimed = true;
  if (!navigator.locks) {
    leader = true;
    return;
  }
  void navigator.locks.request('atrium-notifications', () => {
    leader = true;
    return new Promise<never>(() => {});
  });
}

export function showBrowserNotification(n: { tag: string; title: string; body: string; icon?: string; onClick?: () => void }, opts: { test?: boolean } = {}) {
  if (notifyPermission() !== 'granted') return false;
  if (!opts.test && (!leader || !useUi.getState().notify)) return false;
  try {
    const note = new Notification(n.title, { body: n.body, tag: n.tag, icon: n.icon ?? '/notify-icon.png' });
    note.onclick = () => {
      window.focus();
      n.onClick?.();
      note.close();
    };
    return true;
  } catch {
    // Chrome on Android only allows notifications from a service worker
    return false;
  }
}

export function sendTestNotification() {
  return showBrowserNotification(
    { tag: 'atrium-test', title: 'Atrium', body: "Notifications are on. You'll see mentions, assignments and messages like this." },
    { test: true },
  );
}
